import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Input,
  Form,
  Select,
  Typography,
  Alert,
  Row,
  Col,
  Statistic,
  Divider,
  Progress,
  Badge,
  Space,
  Upload,
  Switch,
  message,
} from "antd";
import {
  WhatsAppOutlined,
  UserOutlined,
  ScheduleOutlined,
  FileImageOutlined,
  FileTextOutlined,
  CheckOutlined,
  ExclamationOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import whatsappService from "../utils/whatsappService";

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const WhatsAppPage = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState("");
  const [result, setResult] = useState(null);
  const [charCount, setCharCount] = useState(0);
  const [sendProgress, setSendProgress] = useState(null);
  const [instanceStatus, setInstanceStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [messageType, setMessageType] = useState("text");

  const recipientGroups = [
    { label: "Single Recipient", value: "single" },
    { label: "All Borrowers (1,234)", value: "all" },
    { label: "Active Loans (890)", value: "active" },
    { label: "Delinquent Loans (154)", value: "delinquent" },
    { label: "Completed Loans (190)", value: "completed" },
  ];

  const messageTemplates = {
    paymentReminder: "Hi {name}, your loan payment of ZMW {amount} is due on {date}. Please make payment to avoid late fees. Thank you!",
    loanApproval: "Congratulations {name}! Your loan of ZMW {amount} has been approved. Funds will be disbursed shortly.",
    delinquencyAlert: "URGENT: {name}, your loan payment of ZMW {amount} is overdue. Please contact us immediately to avoid penalties.",
    paymentConfirmation: "Thank you {name}! We have received your payment of ZMW {amount}. Your next payment is due on {date}.",
  };

  useEffect(() => {
    checkInstanceStatus();
  }, []);

  const checkInstanceStatus = async () => {
    setCheckingStatus(true);
    try {
      const validation = whatsappService.validateConfiguration();
      if (!validation.isValid) {
        setInstanceStatus({
          connected: false,
          error: validation.errors.join(", "),
        });
        return;
      }

      const status = await whatsappService.getInstanceStatus();
      setInstanceStatus({
        connected: status.success,
        data: status.data,
        error: status.error,
      });
    } catch (error) {
      setInstanceStatus({
        connected: false,
        error: error.message,
      });
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleSend = async (values) => {
    setLoading(true);
    setResult(null);
    setSendProgress(null);

    try {
      const recipients = await getRecipients(
        values.recipientType,
        values.customNumbers,
        values.singlePhone
      );

      if (recipients.length === 0) {
        throw new Error("No recipients found");
      }

      // Handle single recipient differently
      if (values.recipientType === "single" && recipients.length === 1) {
        let response;
        if (messageType === "text") {
          response = await whatsappService.sendTextMessage(recipients[0], values.message);
        } else if (messageType === "image" && values.imageUrl) {
          response = await whatsappService.sendImageMessage(recipients[0], values.imageUrl, values.message);
        } else if (messageType === "document" && values.documentUrl) {
          response = await whatsappService.sendDocumentMessage(recipients[0], values.documentUrl, values.filename || "document", values.message);
        }
        
        const resultData = {
          success: response.success,
          summary: {
            total: 1,
            sent: response.success ? 1 : 0,
            failed: response.success ? 0 : 1
          },
          results: [{
            recipient: recipients[0],
            status: response.success ? 'sent' : 'failed',
            messageId: response.messageId,
            error: response.error
          }]
        };
        setResult(resultData);
        
        // Show immediate success/error message
        if (response.success) {
          form.resetFields();
          message.success('WhatsApp message sent successfully!');
        } else {
          message.error(`Failed to send message: ${response.error}`);
        }
      } else {
        // Handle bulk messages
        if (messageType === "text") {
          const response = await whatsappService.sendBulkMessages(
            recipients,
            values.message,
            (progress) => {
              setSendProgress(progress);
            }
          );
          setResult(response);
          
          // Show bulk message success
          if (response.success) {
            form.resetFields();
            message.success(
              `WhatsApp messages sent successfully to ${response.summary.sent} recipients!`
            );
          }
        } else if (messageType === "image" && values.imageUrl) {
          const response = await whatsappService.sendBulkMessages(
            recipients.map(recipient => ({
              phone: recipient,
              type: "image",
              imageUrl: values.imageUrl,
              caption: values.message
            })),
            null,
            (progress) => {
              setSendProgress(progress);
            }
          );
          setResult(response);
          
          // Show bulk image message success
          if (response.success) {
            form.resetFields();
            message.success(
              `WhatsApp images sent successfully to ${response.summary.sent} recipients!`
            );
          }
        }
      }

    } catch (error) {
      message.error("Failed to send WhatsApp messages: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getRecipients = async (type, customNumbers, singlePhone) => {
    if (type === "single") {
      return singlePhone ? [singlePhone.trim()] : [];
    }
    
    if (type === "custom") {
      return customNumbers.split("\n").filter((num) => num.trim());
    }
    
    return Array.from(
      { length: 100 },
      (_, i) => `26096${Math.floor(Math.random() * 1000000)}`
    );
  };

  const handleTemplateSelect = (templateKey) => {
    const template = messageTemplates[templateKey];
    if (template) {
      form.setFieldsValue({ message: template });
      setPreview(template);
      setCharCount(template.length);
    }
  };

  const renderConnectionStatus = () => {
    if (checkingStatus) {
      return <Badge status="processing" text="Checking connection..." />;
    }

    if (!instanceStatus) {
      return <Badge status="default" text="Unknown" />;
    }

    if (instanceStatus.connected) {
      return <Badge status="success" text="Connected" />;
    }

    return <Badge status="error" text={`Error: ${instanceStatus.error}`} />;
  };

  const uploadProps = {
    name: 'file',
    multiple: false,
    accept: 'image/*,.pdf,.doc,.docx',
    beforeUpload: (file) => {
      const isValidType = file.type?.startsWith('image/') || 
                         file.type === 'application/pdf' ||
                         file.type === 'application/msword' ||
                         file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      
      if (!isValidType) {
        message.error('You can only upload image or document files!');
        return false;
      }
      
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('File must be smaller than 10MB!');
        return false;
      }
      
      return false; // Prevent auto upload
    },
  };

  return (
    <div className="whatsapp-page">
      <Title level={3} className="page-title">
        <WhatsAppOutlined style={{ color: "#25D366" }} /> WhatsApp Messaging
      </Title>

      <Row gutter={24}>
        <Col span={16}>
          <Card>
            <div style={{ marginBottom: 16 }}>
              <Text strong>Connection Status: </Text>
              {renderConnectionStatus()}
              <Button
                size="small"
                style={{ marginLeft: 8 }}
                onClick={checkInstanceStatus}
                loading={checkingStatus}
              >
                Refresh
              </Button>
            </div>

            <Form form={form} onFinish={handleSend} layout="vertical">
              {/* Message Type Selection */}
              <Form.Item label="Message Type">
                <Select
                  value={messageType}
                  onChange={setMessageType}
                  style={{ width: 200 }}
                >
                  <Option value="text">
                    <WhatsAppOutlined /> Text Message
                  </Option>
                  <Option value="image">
                    <FileImageOutlined /> Image Message
                  </Option>
                  <Option value="document">
                    <FileTextOutlined /> Document Message
                  </Option>
                </Select>
              </Form.Item>

              {/* Recipient Selection */}
              <Form.Item
                label="Select Recipients"
                name="recipientType"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="single">Single Recipient</Option>
                  <Option value="all">All Borrowers</Option>
                  <Option value="active">Active Loans</Option>
                  <Option value="delinquent">Delinquent Loans</Option>
                  <Option value="custom">Custom Numbers</Option>
                </Select>
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prev, current) =>
                  prev.recipientType !== current.recipientType
                }
              >
                {({ getFieldValue }) => {
                  const recipientType = getFieldValue("recipientType");
                  
                  if (recipientType === "single") {
                    return (
                      <Form.Item
                        name="singlePhone"
                        label="Phone Number"
                        rules={[
                          { required: true, message: "Please enter a phone number" },
                          { pattern: /^[0-9+\-\s()]+$/, message: "Please enter a valid phone number" }
                        ]}
                      >
                        <Input
                          placeholder="260971234567 or 0971234567"
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    );
                  }
                  
                  if (recipientType === "custom") {
                    return (
                      <Form.Item
                        name="customNumbers"
                        label="Enter Phone Numbers (One per line)"
                        rules={[{ required: true }]}
                      >
                        <TextArea
                          rows={4}
                          placeholder="260971234567&#10;260963456789&#10;0977123456"
                        />
                      </Form.Item>
                    );
                  }
                  
                  return null;
                }}
              </Form.Item>

              {/* File Upload for Media Messages */}
              {(messageType === "image" || messageType === "document") && (
                <Form.Item
                  label={messageType === "image" ? "Upload Image" : "Upload Document"}
                  name="fileUrl"
                  rules={[{ required: true, message: "Please upload a file" }]}
                >
                  <Upload {...uploadProps}>
                    <Button icon={<UploadOutlined />}>
                      Click to Upload {messageType === "image" ? "Image" : "Document"}
                    </Button>
                  </Upload>
                </Form.Item>
              )}

              {/* Message Composition */}
              <Form.Item
                label={messageType === "text" ? "WhatsApp Message" : "Caption (Optional)"}
                name="message"
                rules={[
                  { required: messageType === "text" },
                  { max: 4096, message: "Message cannot exceed 4096 characters" },
                ]}
              >
                <TextArea
                  rows={4}
                  showCount
                  maxLength={4096}
                  onChange={(e) => setCharCount(e.target.value.length)}
                  placeholder="Hello {name}, this is a WhatsApp message from ZedLenders..."
                />
              </Form.Item>

              {/* Message Templates */}
              <Form.Item label="Quick Templates">
                <Space wrap>
                  <Button
                    size="small"
                    onClick={() => handleTemplateSelect("paymentReminder")}
                  >
                    Payment Reminder
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handleTemplateSelect("loanApproval")}
                  >
                    Loan Approval
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handleTemplateSelect("delinquencyAlert")}
                  >
                    Delinquency Alert
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handleTemplateSelect("paymentConfirmation")}
                  >
                    Payment Confirmation
                  </Button>
                </Space>
              </Form.Item>

              {/* Preview and Send */}
              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    icon={<WhatsAppOutlined />}
                    disabled={!instanceStatus?.connected}
                  >
                    Send WhatsApp Messages
                  </Button>

                  <Button
                    onClick={() => setPreview(form.getFieldValue("message"))}
                  >
                    Preview Message
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>

          {/* Progress Section */}
          {sendProgress && (
            <Card title="Sending Progress" style={{ marginTop: 24 }}>
              <Progress
                percent={Math.round(sendProgress.progress)}
                status={sendProgress.progress === 100 ? "success" : "active"}
              />
              <div style={{ marginTop: 8 }}>
                <Text>
                  {sendProgress.current} of {sendProgress.total} messages sent
                </Text>
                <br />
                <Text type="success">
                  <CheckOutlined /> Successful: {sendProgress.successCount}
                </Text>
                {sendProgress.failedCount > 0 && (
                  <>
                    <br />
                    <Text type="danger">
                      <ExclamationOutlined /> Failed: {sendProgress.failedCount}
                    </Text>
                  </>
                )}
              </div>
            </Card>
          )}

          {/* Preview Section */}
          {preview && (
            <Card title="Message Preview" style={{ marginTop: 24 }}>
              <div className="whatsapp-preview" style={{ 
                backgroundColor: "#f0f0f0",
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid #d9d9d9"
              }}>
                <div className="message-content" style={{ 
                  whiteSpace: "pre-wrap",
                  fontFamily: "system-ui, -apple-system, sans-serif"
                }}>
                  {preview}
                </div>
                <div className="message-meta" style={{ 
                  fontSize: "12px",
                  color: "#666",
                  marginTop: "8px"
                }}>
                  Length: {preview.length}/4096 characters
                </div>
              </div>
            </Card>
          )}
        </Col>

        {/* Statistics Sidebar */}
        <Col span={8}>
          <Card title="WhatsApp Statistics">
            <Statistic
              title="Instance Status"
              value={instanceStatus?.connected ? "Active" : "Inactive"}
              valueStyle={{
                color: instanceStatus?.connected ? "#3f8600" : "#cf1322",
              }}
            />
            <Divider />

            <Statistic title="Messages Sent Today" value={0} suffix="msgs" />
            <Divider />

            <Statistic
              title="Delivery Rate"
              value={0}
              precision={1}
              suffix="%"
            />
          </Card>

          <Card title="WhatsApp Features" style={{ marginTop: 24 }}>
            <div style={{ marginBottom: 16 }}>
              <Text strong>✓ Rich Media Support</Text>
              <br />
              <Text type="secondary">Images, documents, audio</Text>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>✓ Delivery Confirmation</Text>
              <br />
              <Text type="secondary">Track message delivery</Text>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>✓ Bulk Messaging</Text>
              <br />
              <Text type="secondary">Send to multiple recipients</Text>
            </div>
            <div>
              <Text strong>✓ Template Variables</Text>
              <br />
              <Text type="secondary">{"{name}, {amount}, {date}"}</Text>
            </div>
          </Card>

          <Card title="Quick Actions" style={{ marginTop: 24 }}>
            <Button 
              block 
              icon={<UserOutlined />}
              style={{ marginBottom: 8 }}
            >
              Manage Contacts
            </Button>
            <Button 
              block 
              icon={<ScheduleOutlined />}
              style={{ marginBottom: 8 }}
            >
              Schedule Messages
            </Button>
            <Button 
              block 
              icon={<WhatsAppOutlined />}
              onClick={checkInstanceStatus}
              loading={checkingStatus}
            >
              Check Connection
            </Button>
          </Card>
        </Col>
      </Row>

      {/* Result Display */}
      {result && (
        <Card title="Campaign Results" style={{ marginTop: 24 }}>
          {result.success ? (
            <Alert
              message="WhatsApp Campaign Completed!"
              description={
                <div>
                  <p><strong>Summary:</strong></p>
                  <ul>
                    <li>Total Recipients: {result.summary.total}</li>
                    <li>Successfully Sent: {result.summary.sent}</li>
                    <li>Failed: {result.summary.failed}</li>
                  </ul>
                  {result.summary.failed > 0 && (
                    <p>
                      <Text type="warning">
                        Some messages failed to send. Check the detailed results below.
                      </Text>
                    </p>
                  )}
                </div>
              }
              type={result.summary.failed === 0 ? "success" : "warning"}
              showIcon
            />
          ) : (
            <Alert
              message="Campaign Failed"
              description={result.error}
              type="error"
              showIcon
            />
          )}
        </Card>
      )}
    </div>
  );
};

export default WhatsAppPage;